/**
 * The boundary: exercise responses turned into what the screens read.
 *
 * Its own file rather than private to `exercisePlansService` so that the
 * suites can reach it. A mock stands in for that service, and a mock that
 * imported the module it replaces would be a cycle — so the conversions the
 * mock wants to run its fixtures through live here, one import away from
 * both. The same reasoning that keeps `toPlanSummary` out of
 * `mealPlansService`.
 *
 * Nothing else should read these. The service re-exports them, and the rest of
 * the module deals in `ExercisePlanSummary` and `ExerciseEntry`.
 */

import {
  createId,
  durationParts,
  shortPlanName,
  totalMinutes,
} from "./exerciseMapping";
import type {
  ExerciseEntry,
  ExercisePayload,
  ExercisePlanDetailResponse,
  ExercisePlanExerciseResponse,
  ExercisePlanRecord,
  ExercisePlanSummary,
  ExercisePlanSummaryResponse,
} from "./types/exercisePlans.types";

/** The largest page the endpoint will serve. Anything above is clamped to it. */
export const MAX_PAGE_SIZE = 100;

/**
 * What a list call asks for unless it says otherwise. Ten, matching the ten
 * rows the table shows, so a page of the API is a page of the table.
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * One stored exercise, as the dialog and the view hold one.
 *
 * The `id` is the browser's own rather than the backend's number. Nothing
 * writes an exercise yet, so the stored id has nothing to address — and the
 * dialog's rows are keyed on it, where a row added beside a fetched one must
 * not be able to collide with it.
 *
 * A row with no name at all is still taken: dropping it would renumber every
 * line after it against a plan the backend says has five exercises on it.
 */
const toExerciseEntry = (
  exercise: ExercisePlanExerciseResponse
): ExerciseEntry => ({
  id: createId("exercise"),
  type: exercise?.exercise_name?.trim() ?? "",
  // Absent and null mean the same thing here: an exercise no catalog row is
  // behind. `undefined` would read back as a missing field rather than as the
  // answer the backend actually gave.
  typeId: exercise?.exercise_type_id ?? null,
  minutes: totalMinutes({
    hours: exercise?.duration_hours,
    minutes: exercise?.duration_minutes,
  }),
});

/** The exercises on a plan, in the order the backend put them in. */
const toExerciseEntries = (
  exercises: ExercisePlanExerciseResponse[] | null | undefined
): ExerciseEntry[] =>
  [...(exercises ?? [])]
    .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
    .map(toExerciseEntry);

/**
 * One row, in the shape the table reads.
 *
 * Every field is defended: a plan with no client, no date or no duration block
 * is a row the list still has to render, and a dash in a cell is a better
 * answer than a screen that fails to draw. If the backend's field names turn
 * out to differ from these, this function is the only thing that has to
 * change.
 *
 * `types` comes off `exercise_names`, which the list row carries — so the
 * table names the exercises on a plan without opening it. A plan with none
 * reads as an empty list rather than as a row that failed to say.
 */
export const toExercisePlanSummary = (
  row: ExercisePlanSummaryResponse
): ExercisePlanSummary => {
  const name = row?.name?.trim() || "Untitled plan";

  return {
    id: row?.public_id ?? "",
    name,
    shortName: shortPlanName(name),
    clientId: row?.client?.public_id ?? "",
    clientName: row?.client?.name?.trim() || "Unnamed client",
    createdOn: row?.created_at ?? null,
    // The backend's own count rather than the length of whatever came with it:
    // the row is the authority on how many exercises are on the plan, and the
    // names, when they arrive, are a convenience beside it.
    exerciseCount: row?.exercise_count ?? 0,
    minutes: totalMinutes(row?.total_duration),
    lastSentAt: row?.last_sent_at ?? null,
    types: (row?.exercise_names ?? [])
      .map((name) => name?.trim() ?? "")
      .filter(Boolean),
  };
};

/** One plan whole, which is a row plus what is actually on it. */
export const toExercisePlanRecord = (
  detail: ExercisePlanDetailResponse
): ExercisePlanRecord => {
  const entries = toExerciseEntries(detail?.exercises);

  return {
    ...toExercisePlanSummary(detail),
    // The detail carries the exercises whole, so its own names beat the
    // summary's reading of `exercise_names` — same names, one source.
    types: entries.map((entry) => entry.type).filter(Boolean),
    entries,
  };
};

/**
 * One line of the dialog, as a create or an update takes it.
 *
 * Which of the two shapes it goes up as is decided by `typeId` alone: a line
 * carrying one was picked off the catalog and travels as that row's id, and a
 * line without one was typed and travels as its name. Never both — the
 * backend reads an exercise with an `exercise_type_id` as a catalog row whatever
 * name sits beside it, so sending the name too would only give it a chance to
 * disagree with the catalog.
 *
 * The duration is split back into the pair the wire counts in, which is the
 * one place that conversion happens going out.
 */
export const toExercisePayload = (entry: ExerciseEntry): ExercisePayload => {
  const { hours, minutes } = durationParts(entry.minutes);

  return entry.typeId == null
    ? {
        exercise_name: entry.type.trim(),
        duration_hours: hours,
        duration_minutes: minutes,
      }
    : {
        exercise_type_id: entry.typeId,
        duration_hours: hours,
        duration_minutes: minutes,
      };
};

/**
 * Every line worth storing, in the order they are on the page.
 *
 * A line with nothing picked and nothing typed is dropped: it is a row the
 * dietitian opened and did not use, not an exercise with no name. The dialog
 * drops these too, so this is a second guard rather than the only one — but
 * this is the one the request passes through.
 */
export const toExercisePayloads = (
  entries: ExerciseEntry[]
): ExercisePayload[] =>
  entries
    .filter((entry) => entry.typeId != null || entry.type.trim())
    .map(toExercisePayload);
